import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { Button, Drawer, Spin } from 'antd'

import {
  BarChartOutlined,
  DatabaseOutlined,
  LeftOutlined,
  RightOutlined,
  TableOutlined
} from '@ant-design/icons'

import { selectDataSources } from '@/store/slices/data/selectors'
import { updateShapeData, updateShapeVisualSettings } from '@/store/slices/shapes/actions'
import { selectAllSlideIds, selectCurrentSlideId } from '@/store/slices/slides/selectors'
import { selectCanvasSelectedObject, selectShapeData } from '@store/slices/shapes/selectors'

import {
  createDataTransformation,
  updateDataTransformation
} from '@/services/data-transfomations-service'

import { visualSettingsAfterRemap } from '@/components/imagemap/right-panel/data-source/visualSettingsAfterRemap'
import { useDataTable } from '@/components/imagemap/right-panel/hooks'
import DataActions from '@components/imagemap/right-panel/data-actions'

import { convertData, getGlobalNumberFormat, stripComputedMembers } from '../helpers'
import { useClearDataSelection, useOutputData } from '../hooks'
import DataTree from './DataTree'
import InputTableWrapper from './InputTableWrapper'
import OutputTable from './OutputTable'
import Table from './Table'

export default function DefaultDataView() {
  const dispatch = useDispatch()
  const history = useHistory()
  const selectedObject = useSelector(selectCanvasSelectedObject)
  const dataSources = useSelector(selectDataSources)
  const slideIds = useSelector(selectAllSlideIds)
  const currentSlideId = useSelector(selectCurrentSlideId)
  const [rightIsOpen, setRightIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { projectId, dataSelection, dataTable } = useSelector(selectShapeData)

  const [dataTableId, setDataTableId] = useState(() => dataTable?.id || null)
  const { data: dataTableInstance } = useDataTable(dataTableId) // this is for original table
  // Hide materialized nets from the Input table but keep them selectable elsewhere (issue #701).
  const { colHeaders, rowHeaders, tableData } = useMemo(() => {
    const stripped = stripComputedMembers(
      convertData(dataTableInstance) || [],
      dataTableInstance?.index || [],
      dataTableInstance?.headers || [],
      dataTableInstance?.computedRows,
      dataTableInstance?.computedColumns
    )
    return {
      colHeaders: stripped.colHeaders,
      rowHeaders: stripped.rowHeaders,
      tableData: stripped.data
    }
  }, [dataTableInstance])
  const [selections, setSelections] = useState([])
  const receiveSelections = useCallback(value => setSelections(value), [])
  useEffect(() => setSelections([]), [dataTableId])
  const columnNames = useMemo(
    () =>
      (colHeaders[colHeaders.length - 1] || []).flatMap(cell =>
        Array(cell.span || 1).fill(cell.name)
      ),
    [colHeaders]
  )

  const breadcrumb = useMemo(() => {
    for (const source of dataSources) {
      for (const section of source.dataSections || []) {
        const table = section.dataTables?.find(t => t.id === dataTableId)
        if (table) {
          return { source: source.filename, section: section.name, table: table.name }
        }
      }
    }
    return null
  }, [dataSources, dataTableId])

  const slideNumber = slideIds.indexOf(selectedObject?.slide ?? currentSlideId) + 1
  const formatCode = useMemo(
    () => selectedObject?.visualSettings?.numberFormat,
    [selectedObject?.visualSettings?.numberFormat]
  )

  const [outputData, outputDataDispatch] = useOutputData(dataSelection?.dataFrame, formatCode)

  const transformData = useCallback(() => {
    if (dataSelection?.dataFrame) {
      const { data, index, columns } = dataSelection.dataFrame
      outputDataDispatch({
        type: 'transform',
        data: data,
        rowHeaders: index,
        colHeaders: columns,
        formatCode: formatCode
      })
    }
  }, [dataSelection?.dataFrame, outputDataDispatch, formatCode])

  useEffect(() => {
    transformData()
  }, [transformData])

  const updateData = async payload => {
    try {
      const { dataFrame, shapeData } = await updateDataTransformation(dataSelection.id, payload)
      dispatch(updateShapeData(selectedObject.id, shapeData, false))
      return dataFrame
    } catch (error) {
      console.error(error.message)
    }
  }

  const createData = async payload => {
    try {
      const { dataFrame, shapeData } = await createDataTransformation({
        ...payload,
        shape: selectedObject.pk
      })
      dispatch(updateShapeData(selectedObject.id, shapeData))
      return dataFrame
    } catch (error) {
      console.error(error.message)
    }
  }

  const updateNumberFormat = (numberFormat, isUpdate: boolean) => {
    if (numberFormat || selectedObject.visualSettings) {
      const visualSettings = visualSettingsAfterRemap(selectedObject.visualSettings, numberFormat, {
        isUpdate
      })
      dispatch(updateShapeVisualSettings(selectedObject.id, visualSettings, false))
    }
  }

  const handleSelectData = async (activeRowTags, selectionStrategy) => {
    const [rows, columns] = selections
    selectionStrategy = {
      freeform: 'by_absolute_position',
      column: 'by_column_position',
      row: 'by_column_position',
      group: 'by_group_inclusion'
    }[selectionStrategy]
    const payload = {
      rows,
      columns,
      selectionStrategy,
      dataTable: dataTableId,
      rowTags: activeRowTags
    }

    setIsLoading(true)
    const isUpdate = dataSelection != null
    let dataFrame
    if (isUpdate) {
      dataFrame = await updateData(payload)
    } else {
      dataFrame = await createData(payload)
    }
    if (dataFrame != null) {
      const code = getGlobalNumberFormat(dataTableInstance?.tableMeta?.numberFormat, rows, columns)
      outputDataDispatch({
        type: 'set_data',
        data: dataFrame.data,
        rowHeaders: dataFrame.index,
        colHeaders: dataFrame.columns,
        formatCode: code
      })
      updateNumberFormat(code, isUpdate)
    }
    setIsLoading(false)
  }

  const clearDataSelection = useClearDataSelection({
    projectId,
    shape: selectedObject,
    outputDataDispatch
  })

  return (
    <Spin wrapperClassName="insert insert-workbench" spinning={isLoading}>
      <header className="workbench-header">
        <Button icon={<LeftOutlined />} onClick={() => history.goBack()}>
          Back to slides
        </Button>
        <span className="header-divider" />
        <nav className="breadcrumb">
          {breadcrumb ? (
            <>
              <DatabaseOutlined />
              <span>{breadcrumb.source}</span>
              <RightOutlined className="chevron" />
              <span>{breadcrumb.section}</span>
              <RightOutlined className="chevron" />
              <span className="current">{breadcrumb.table}</span>
            </>
          ) : (
            <span>No table selected</span>
          )}
        </nav>
        {selectedObject?.name && slideNumber > 0 && (
          <span className="shape-chip">
            {selectedObject.type === 'table' ? <TableOutlined /> : <BarChartOutlined />}
            {selectedObject.name} · Slide {slideNumber}
          </span>
        )}
      </header>

      <div className="workbench-body">
        <DataTree dataTableId={dataTableId} onSelectTable={setDataTableId} />

        <InputTableWrapper
          hasData={tableData.length > 0}
          rowMetaTags={dataTableInstance?.rowsMeta || []}
          handleSelectData={handleSelectData}
          selections={selections as [number[], number[]] | []}
          rowNames={rowHeaders}
          columnNames={columnNames}
          isSavedTable={dataTableId === dataTable?.id}
        >
          {props => (
            <Table
              {...props}
              modern
              sendSelections={receiveSelections}
              data={tableData}
              colHeaders={colHeaders}
              rowHeaders={rowHeaders}
              tableMeta={dataTableInstance?.tableMeta?.tags}
              selectionMode="multiple"
            />
          )}
        </InputTableWrapper>

        <OutputTable
          modern
          setIsLoading={setIsLoading}
          data={outputData.data}
          clearDataSelection={clearDataSelection}
          rowHeaders={outputData.rowHeaders}
          colHeaders={outputData.colHeaders}
          setRightIsOpen={setRightIsOpen}
          showLabelToggles={selectedObject?.type === 'table'}
          labelSettings={selectedObject?.visualSettings?.table}
          onLabelSettingsChange={async labels => {
            await dispatch(
              updateShapeVisualSettings(selectedObject.id, {
                table: { ...selectedObject.visualSettings?.table, ...labels }
              })
            )
          }}
        />
      </div>

      <Drawer
        title="Data Transformations"
        placement="right"
        width={360}
        visible={rightIsOpen}
        onClose={() => setRightIsOpen(false)}
        destroyOnClose
      >
        <DataActions />
      </Drawer>
    </Spin>
  )
}
